import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/i18n/LanguageContext";
import SiteJsonLd from "./components/SiteJsonLd";
import Index from "./pages/Index";

// Code-split: every non-home route is loaded on demand to keep
// the initial bundle small (LCP/TTI win on the homepage).
const Services = lazy(() => import("./pages/Services"));
const AgenticAI = lazy(() => import("./pages/AgenticAI"));
const GenerativeAI = lazy(() => import("./pages/GenerativeAI"));
const PredictiveAI = lazy(() => import("./pages/PredictiveAI"));
const DataPlatforms = lazy(() => import("./pages/DataPlatforms"));
const Solutions = lazy(() => import("./pages/Solutions"));
const AIAgentsWorkflowAutomation = lazy(() => import("./pages/solutions/AIAgentsWorkflowAutomation"));
const DocumentIntelligence = lazy(() => import("./pages/solutions/DocumentIntelligence"));
const KnowledgeSystems = lazy(() => import("./pages/solutions/KnowledgeSystems"));
const CustomerSupportSystems = lazy(() => import("./pages/solutions/CustomerSupportSystems"));
const PredictiveModelsDecisionScience = lazy(() => import("./pages/solutions/PredictiveModelsDecisionScience"));
const DataPlatformsForAI = lazy(() => import("./pages/solutions/DataPlatformsForAI"));
const Industries = lazy(() => import("./pages/Industries"));
const AccountingFirms = lazy(() => import("./pages/industries/AccountingFirms"));
const ManufacturingDistribution = lazy(() => import("./pages/industries/ManufacturingDistribution"));
const ServiceBusinesses = lazy(() => import("./pages/industries/ServiceBusinesses"));
const EcommerceOperations = lazy(() => import("./pages/industries/EcommerceOperations"));
const VetAIVendor = lazy(() => import("./pages/authority/VetAIVendor"));
const ProductionGradeAI = lazy(() => import("./pages/authority/ProductionGradeAI"));
const WorkflowsDeserveAI = lazy(() => import("./pages/authority/WorkflowsDeserveAI"));
const About = lazy(() => import("./pages/About"));
const Insights = lazy(() => import("./pages/Insights"));
const Contact = lazy(() => import("./pages/Contact"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    <LanguageProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <SiteJsonLd />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/agentic-ai" element={<AgenticAI />} />
                <Route path="/services/generative-ai" element={<GenerativeAI />} />
                <Route path="/services/predictive-ai" element={<PredictiveAI />} />
                <Route path="/services/data-platforms" element={<DataPlatforms />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/solutions/ai-agents-workflow-automation" element={<AIAgentsWorkflowAutomation />} />
                <Route path="/solutions/document-intelligence" element={<DocumentIntelligence />} />
                <Route path="/solutions/knowledge-systems-enterprise-retrieval" element={<KnowledgeSystems />} />
                <Route path="/solutions/customer-support-service-systems" element={<CustomerSupportSystems />} />
                <Route path="/solutions/predictive-models-decision-science" element={<PredictiveModelsDecisionScience />} />
                <Route path="/solutions/data-platforms-for-ai" element={<DataPlatformsForAI />} />
                <Route path="/industries" element={<Industries />} />
                <Route path="/industries/accounting-firms" element={<AccountingFirms />} />
                <Route path="/industries/manufacturing-distribution" element={<ManufacturingDistribution />} />
                <Route path="/industries/service-businesses" element={<ServiceBusinesses />} />
                <Route path="/industries/ecommerce-operations" element={<EcommerceOperations />} />
                <Route path="/insights/vet-ai-vendor" element={<VetAIVendor />} />
                <Route path="/insights/production-grade-ai" element={<ProductionGradeAI />} />
                <Route path="/insights/workflows-deserve-ai" element={<WorkflowsDeserveAI />} />
                <Route path="/about" element={<About />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/insights/:slug" element={<ArticleDetail />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </LanguageProvider>
  </ThemeProvider>
);

export default App;
